import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { selectAuthFeatureFlags } from '../../../auth/AuthSelectors';
import { SearchFilterContentTypes } from '../../Shared/enums';
import { useConversationalSearch } from '../components/navSearch/NavSearchContext';
import SearchFilterOptions from '../entities/SearchFilterOptions';
import { isConversationalSearchEnabledForContentClass } from '../utils/conversationalSearch';

// Re-exported from its pure home in utils so existing callers keep this import
// path; selectors import directly from utils to avoid depending on this hook.
export { isConversationalSearchEnabledForContentClass };

const DEFAULT_AI_PLACEHOLDER =
  "Describe what you need... and we'll find what fits...";
const CONV_SEARCH_PATH = '/ai/conv-search/new';

const HYDRATION_MISMATCH_ROUTES = new Set([
  '/video',
  '/audio',
  '/images',
  '/audio/music',
  '/audio/sound-effects',
  '/video/footage',
  '/video/motion-backgrounds',
  '/video/templates',
]);

function isOnHydrationMismatchRoute(): boolean {
  const globalScope = (typeof window === 'undefined' ? global : window) as {
    __CURRENT_PATH__?: string;
  };
  const path = (globalScope.__CURRENT_PATH__ ?? '')
    .split('?')[0]
    .replace(/\/$/, '')
    .toLowerCase();
  return HYDRATION_MISMATCH_ROUTES.has(path);
}

interface UseSearchWithAIOptions {
  showConversationalSearchCta: boolean;
}

interface UseSearchWithAIResult {
  showAiCta: boolean;
  conversationalSearchEnabled: boolean;
  setConversationalSearchEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  getPlaceholder: (
    defaultPlaceholder: string | undefined,
    contentType: SearchFilterContentTypes
  ) => string;
  handleConversationalRedirect: (
    searchTerm: string,
    searchOrigin: string,
    contentType: SearchFilterContentTypes
  ) => boolean;
  goToConversationalSearch: (
    searchTerm: string,
    searchOrigin: string,
    contentType: SearchFilterContentTypes
  ) => void;
  isSearchWithAIEnabledForContentType: (contentType: string) => boolean;
}

export function useSearchWithAI({
  showConversationalSearchCta,
}: UseSearchWithAIOptions): UseSearchWithAIResult {
  const { conversationalSearchEnabled, setConversationalSearchEnabled } =
    useConversationalSearch();
  const { showConversationalSearchAudio } = useSelector(selectAuthFeatureFlags);

  const [isHydrating, setIsHydrating] = useState(isOnHydrationMismatchRoute);
  useEffect((): void => setIsHydrating(false), []);
  const showAiCta = !isHydrating && showConversationalSearchCta;

  const isSearchWithAIEnabledForContentType = useCallback(
    (contentType: string): boolean => {
      const contentClass = SearchFilterOptions.getContentClassFromContentType(
        contentType as SearchFilterContentTypes
      );

      return isConversationalSearchEnabledForContentClass(
        contentClass,
        showConversationalSearchAudio
      );
    },
    [showConversationalSearchAudio]
  );

  const getPlaceholder = (
    defaultPlaceholder: string | undefined,
    contentType: SearchFilterContentTypes
  ): string => {
    if (!conversationalSearchEnabled) {
      return defaultPlaceholder ?? '';
    }

    const placeholders = {
      [SearchFilterContentTypes.All_videos_content_type]:
        "Describe the scene you're looking for",
      [SearchFilterContentTypes.Footage]:
        "Describe the footage you're looking for",
      [SearchFilterContentTypes.Motion_bgs]: 'Describe the graphic you want',
      [SearchFilterContentTypes.Templates]:
        "Describe the template you're looking for",
      ...(showConversationalSearchAudio && {
        [SearchFilterContentTypes.All_audio_content_type]:
          "Describe the audio you're looking for",
        [SearchFilterContentTypes.Music]:
          "Describe the music you're looking for",
        [SearchFilterContentTypes.Sound_effects]:
          "Describe the sound effect you're looking for",
      }),
    };

    return placeholders[contentType] ?? DEFAULT_AI_PLACEHOLDER;
  };

  // Navigate to conversational search unconditionally. Used directly by the
  // `button` discoverability arm, whose dedicated CTA routes to conversational
  // search regardless of the (absent) in-bar toggle state.
  const goToConversationalSearch = (
    searchTerm: string,
    searchOrigin: string,
    contentType: SearchFilterContentTypes
  ): void => {
    const url = new URL(CONV_SEARCH_PATH, window.location.origin);
    const params = new URLSearchParams({
      search_origin: searchOrigin,
      prompt: searchTerm,
    });
    const contentClass =
      SearchFilterOptions.getContentClassFromContentType(contentType);
    if (contentClass) {
      params.set('content-class', contentClass);
    }
    url.search = params.toString();
    window.location.href = url.toString();
  };

  const handleConversationalRedirect = (
    searchTerm: string,
    searchOrigin: string,
    contentType: SearchFilterContentTypes
  ): boolean => {
    if (!conversationalSearchEnabled) return false;

    goToConversationalSearch(searchTerm, searchOrigin, contentType);
    return true;
  };

  return {
    showAiCta,
    conversationalSearchEnabled,
    setConversationalSearchEnabled,
    getPlaceholder,
    handleConversationalRedirect,
    goToConversationalSearch,
    isSearchWithAIEnabledForContentType,
  };
}
